import React from 'react';
import type { PillShape } from './todayModel';

interface PillGlyphProps {
  shape: PillShape;
  /** Supplements' accent — used only when `next` (the ring and the pill). */
  accent?: string;
  /** The day's NEXT dose: the plate gets an accent ring and the pill turns accent. */
  next?: boolean;
  /** Recorded or later in the day: the pill steps back. */
  quiet?: boolean;
  /** The plate's diameter in px (default 36). */
  size?: number;
}

/**
 * ⭐ A small drawn pill on a porcelain plate (the Supplements pass, step 2) — the list's icon for a
 * dose, by its shape: softgel, capsule, tablet, scoop, drop, gummy, sachet. Neutral (the pill is
 * the muted foreground, so it reads in both themes); the NEXT dose alone is lit in the accent.
 * Decorative — the row's text says everything it shows — so it is hidden from assistive tech.
 */
export function PillGlyph({ shape, accent, next = false, quiet = false, size = 36 }: PillGlyphProps) {
  const lit = next && !!accent;
  return (
    <span
      aria-hidden="true"
      data-pill={shape}
      data-next={lit ? '' : undefined}
      className={`relative inline-grid place-items-center shrink-0 rounded-full bg-[rgb(var(--foreground)/0.05)] shadow-[inset_0_0_0_1px_rgb(var(--foreground)/0.12)] ${lit ? '' : 'text-muted-foreground'} ${quiet ? 'opacity-60' : ''}`}
      style={{
        width: size,
        height: size,
        ...(lit ? { color: accent, outline: `1.5px solid ${accent}`, outlineOffset: 2 } : null),
      }}
    >
      <svg width={size} height={size} viewBox="-20 -20 40 40" fill="none" stroke="currentColor" strokeWidth={1} strokeLinejoin="round">
        {SHAPES[shape]}
      </svg>
    </span>
  );
}

/** The body tint, the outline and the glint every shape shares. */
const BODY = { fill: 'currentColor', fillOpacity: 0.4, strokeOpacity: 0.85 } as const;
const GLINT = { fill: '#fff', fillOpacity: 0.28, stroke: 'none' } as const;

const SHAPES: Record<PillShape, React.ReactNode> = {
  softgel: (
    <g transform="rotate(-32)">
      <ellipse rx={11.5} ry={6.2} {...BODY} />
      <ellipse cx={-4.2} cy={-2.8} rx={4.8} ry={1.4} {...GLINT} />
    </g>
  ),
  capsule: (
    <g transform="rotate(34)">
      <rect x={-12.5} y={-5} width={25} height={10} rx={5} fill="currentColor" fillOpacity={0.18} strokeOpacity={0.85} />
      <path d="M0 -5 H-7.5 A5 5 0 0 0 -7.5 5 H0 Z" fill="currentColor" fillOpacity={0.55} stroke="none" />
      <path d="M0 -5 V5" strokeOpacity={0.6} />
      <rect x={-9} y={-3.4} width={6} height={1.3} rx={0.65} {...GLINT} />
    </g>
  ),
  tablet: (
    <g>
      <circle r={9} {...BODY} />
      <path d="M-5.6 0.6 H5.6" strokeOpacity={0.7} strokeLinecap="round" />
      <ellipse cx={-3.2} cy={-4.4} rx={3.4} ry={1.2} transform="rotate(-24 -3.2 -4.4)" {...GLINT} />
    </g>
  ),
  // A scoop of powder as its heap (a bowl with a handle read as a magnifying glass).
  scoop: (
    <g>
      <path d="M-11.5 5 C-10.4 -1.6 -5.8 -7.2 0 -7.5 C5.8 -7.2 10.4 -1.6 11.5 5 Q0 7.2 -11.5 5 Z" {...BODY} />
      <ellipse cx={-3.8} cy={-3.2} rx={3.8} ry={1.4} transform="rotate(-18 -3.8 -3.2)" {...GLINT} />
    </g>
  ),
  drop: (
    <g>
      <path d="M0 -11 C4.2 -4.6 7.2 -0.6 7.2 3.6 A7.2 7.2 0 0 1 -7.2 3.6 C-7.2 -0.6 -4.2 -4.6 0 -11 Z" {...BODY} />
      <ellipse cx={-3} cy={3.2} rx={1.3} ry={2.6} {...GLINT} />
    </g>
  ),
  gummy: (
    <g transform="rotate(12)">
      <rect x={-8} y={-8} width={16} height={16} rx={5.5} {...BODY} />
      <ellipse cx={-3} cy={-4.4} rx={3.2} ry={1.2} {...GLINT} />
    </g>
  ),
  sachet: (
    <g transform="rotate(-10)">
      <path d="M-7.5 -8 L-5.6 -10 L-3.7 -8 L-1.9 -10 L0 -8 L1.9 -10 L3.7 -8 L5.6 -10 L7.5 -8 V9.5 A1.5 1.5 0 0 1 6 11 H-6 A1.5 1.5 0 0 1 -7.5 9.5 Z" {...BODY} />
      <path d="M-7.5 -5 H7.5" strokeOpacity={0.55} />
      <rect x={-5.5} y={-2.6} width={2} height={9} rx={1} {...GLINT} />
    </g>
  ),
};
